import React, { useEffect, useState } from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { GlobalProps } from '@bloomberg/media-ui-core/types';
import { CheckmarkFilled, ChevronDown } from '@bloomberg/media-ui-icons';
import { default as FocusTrap } from 'focus-trap-react';

import { REGIONAL_DATA } from '../constants/constants';
import { REGIONS, THEMES } from '../types';
import setExpPrefCookie from '../utils/expPref';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './RegionPicker.module.css';

export type RegionPickerProps = GlobalProps & {
  isRegionPickerOpen?: boolean;
  setIsRegionPickerOpen?: (isOpen: boolean) => void;
  region: REGIONS;
  theme?: THEMES;
  trackingFunction: (eventLabel: string) => void;
};

const ChevronArrow = ({
  isRegionPickerOpen,
  className,
}: {
  className?: string;
  isRegionPickerOpen?: boolean;
}) => {
  return (
    <div
      className={classes([
        className,
        { [styles.arrowDown]: isRegionPickerOpen },
      ])}
    >
      <ChevronDown />
    </div>
  );
};

export const RegionPicker = React.forwardRef<HTMLDivElement, RegionPickerProps>(
  function RegionPickerComponent(props: RegionPickerProps, ref) {
    const {
      isRegionPickerOpen,
      setIsRegionPickerOpen = () => {},
      region = REGIONS.US,
      theme = THEMES.LIGHT,
      trackingFunction,
    } = props;

    const [selectedRegion, setSelectedRegion] = useState<REGIONS>(
      Object.values(REGIONS).includes(region) ? region : REGIONS.US
    );
    const translateMessage = useTranslateMessage();

    useEffect(() => {
      if (Object.values(REGIONS).includes(region)) {
        setSelectedRegion(region);
      }
    }, [region]);

    const onRegionSelected = (value: string) => {
      setSelectedRegion(value as REGIONS);
      setIsRegionPickerOpen(false);
      trackingFunction(`edition-${value}`);
      setExpPrefCookie(value);

      window.location.href = REGIONAL_DATA[value as REGIONS].url;
    };

    const onRegionClicked = () => {
      setIsRegionPickerOpen(!isRegionPickerOpen);
    };

    const closeRegionPicker = () => {
      setIsRegionPickerOpen(false);

      return true;
    };

    const componentName = useSlug('region-picker');

    const cx = classes([
      styles.regionPickerContainer,
      {
        [styles.light]: theme === THEMES.LIGHT,
        [styles.dark]: theme === THEMES.DARK,
        [styles.blue]: theme === THEMES.BLUE,
      },
    ]);

    const { value, text, switchEditionPrompt } = REGIONAL_DATA[selectedRegion];

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsRegionPickerOpen(!isRegionPickerOpen);
      }
    };

    return (
      <FocusTrap
        active={isRegionPickerOpen}
        focusTrapOptions={{
          fallbackFocus: `.${styles.regionPickerContainer}`,
          clickOutsideDeactivates: () => closeRegionPicker(),
          allowOutsideClick: true,
        }}
      >
        <div className={cx} ref={ref} data-component={componentName}>
          <div
            className={styles.region}
            aria-label={translateMessage({
              id: 'media-ui-nav_arialabel_selectRegion',
              defaultMessage: 'Select Region',
            })}
            data-testid="region-button"
            role="button"
            aria-expanded={`${Boolean(isRegionPickerOpen)}`}
            onClick={onRegionClicked}
            tabIndex={0}
            onKeyDown={onKeyDown}
          >
            <div
              className={styles.regionText}
              aria-label={translateMessage({
                id: 'media-ui-nav_arialabel_currentRegion',
                defaultMessage: 'Current Region',
              })}
              data-testid="region"
            >
              {switchEditionPrompt || `${text} Edition`}
            </div>

            <ChevronArrow isRegionPickerOpen={isRegionPickerOpen} />
          </div>
          {isRegionPickerOpen && (
            <div
              className={styles.dropdown}
              aria-label={translateMessage({
                id: 'media-ui-nav_arialabel_regionSelector',
                defaultMessage: 'Region Selector',
              })}
              data-testid="dropdown"
            >
              {Object.values(REGIONAL_DATA).map((option) => {
                return (
                  <button
                    className={styles.option}
                    key={option.value}
                    type="button"
                    onClick={() => onRegionSelected(option.value)}
                  >
                    {option.text}
                    {option.value === value && (
                      <CheckmarkFilled className={styles.checkMark} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </FocusTrap>
    );
  }
);
